// ============================================================================
// THE <600px SWITCH — docs/design/handoff-mobile/README.md §Viewport and device:
// "375 px is the minimum supported width; the layout is single-column and fluid,
// so it reflows without changes. Above 600 px, do not stretch — the app switches
// to the desktop layout."
//
// IT IS A COMPONENT SWITCH, NOT A STYLESHEET. The mobile app is a DIFFERENT
// PRODUCT, not a reflow: five tabs instead of eight rail items, five screens
// instead of thirteen, a capture sheet where the desktop has a ⌘K palette, and
// five desktop screens deliberately absent. Hiding one tree with CSS would mean
// mounting both — every hook in both, every query in both — and would make
// "deliberately absent from mobile" a thing the phone still downloaded, still
// rendered, and still fetched data for.
//
// WHY matchMedia AND NOT window.innerWidth. A media query is the definition the
// handoff states, and `matchMedia` is the only reading of it that agrees with
// what CSS would do — including under the UI-scale zoom (src/lib/scale.ts stamps
// `zoom` on <html>, and media queries resolve against the viewport, not against
// the zoomed layout box). It also gives a change event, so a desktop window
// dragged narrow flips without a resize listener firing on every pixel.
//
// THE DEFAULT IS DESKTOP, always, for every environment that cannot answer: a
// server render, an old browser, a test runner without matchMedia. The desktop
// app is the one that has existed since V0 and the one every existing smoke test
// asserts against; a missing API must never silently swap the whole product.
// ============================================================================

import { useEffect, useState } from 'react'

/** The last width that is still a phone. 600 and above is the desktop app. */
export const MOBILE_MAX_WIDTH = 599

export const MOBILE_QUERY = `(max-width: ${MOBILE_MAX_WIDTH}px)`

/**
 * Does this window want the mobile app? Pure, so the rule can be tested without
 * a browser and without React (see mobile/mobile.test.ts).
 *
 * A window that cannot answer is a desktop window. See the header.
 */
export function readIsMobile(win: Pick<Window, 'matchMedia'> | null | undefined): boolean {
  try {
    return win?.matchMedia?.(MOBILE_QUERY)?.matches === true
  } catch {
    return false
  }
}

/**
 * The switch, as a hook. Subscribes to the query so rotating a phone, splitting
 * an Android screen, or dragging a desktop window past 600px moves the app
 * across immediately rather than at the next reload.
 */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(() =>
    readIsMobile(typeof window === 'undefined' ? null : window),
  )

  useEffect(() => {
    if (typeof window === 'undefined') return
    let list: MediaQueryList
    try {
      list = window.matchMedia(MOBILE_QUERY)
    } catch {
      return
    }
    const onChange = () => setIsMobile(list.matches)
    // Read once on mount too: the first render used the value from before this
    // effect ran, and a hydration or a fast rotation can land between the two.
    onChange()
    // `addListener` is the deprecated form, and it is what happy-dom and older
    // Safari actually implement. Both are tried rather than assumed.
    if (typeof list.addEventListener === 'function') {
      list.addEventListener('change', onChange)
      return () => list.removeEventListener('change', onChange)
    }
    const legacy = list as unknown as {
      addListener?: (fn: () => void) => void
      removeListener?: (fn: () => void) => void
    }
    legacy.addListener?.(onChange)
    return () => legacy.removeListener?.(onChange)
  }, [])

  return isMobile
}
